import { useState, useEffect } from "react";
import DOMPurify from 'dompurify';
import { Buffer } from 'buffer';
import { RefreshCw, CheckCircle2, AlertTriangle, ShieldCheck, Settings, BotMessageSquare, LayoutTemplate, Spotlight,
  CircleCheck, TriangleAlert, CircleX, Clock3, Info, X, MessageCircle
} from "lucide-react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Tooltip  from "@mui/material/Tooltip";
import { Input } from '@CommonFunctionalDisplay/InputBox';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import { MdOutlineGeneratingTokens as TokenIcon } from "react-icons/md";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SquareUserRound, Trash, Cable, ArrowDownToLine} from 'lucide-react';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import WhatsappOAuthService from '@Helpers/URLHelpers/WeboutService/WhatsappOAuthService';
import KaiziqLeadsConnectionService from '@Helpers/URLHelpers/WeboutService/KaiziqLeadsConnectionService';
import WhatsappPhoneNumberService from '@Helpers/URLHelpers/WeboutService/WhatsappPhoneNumberService';
import ErrorHandler from '@Root/ErrorHandler';
import './WhatsappLeadsSettingsPage.scss';


export default function WhatsappLeadsSettingsPage(props) {

    const [cursor, setCursor] = useState('auto');
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('whatsapp');
    const [tokenValue, setTokenValue] = useState("");
    const [prevTokenValue, setPrevTokenValue] = useState("");
    const [tokenStatus, setTokenStatus] = useState(null);
    const [confirmText, setConfirmText] = useState("");
    const [formData, setFormData] = useState({
        accessToken: '',
        title: '',
        wabaId: '',
        appId: '',
        appSecret: '',
        systemUserToken: '',
        phoneNumberId: '',
    });
    const [registerModal, setRegisterModal] = useState({
        open: false,
        item: null,
    });
    const [errors, setErrors] = useState({});
    const [leadsList, setLeadsList] = useState([]);
    const { activeUserId } = useReduxActiveUser();
    const { currentProjectId, currentUserProjectPermissions } = useReduxProject();
    const { currentLeads, currentLeadsId, numbersList, setCurrentLeads, setNumbersList, setPhoneNumber, currentPhoneNumber, clearLeads, whatsappFlowProtection, templateProtection, campaignProtection } = useReduxLeads();
    const [advanceProtection, setAdvanceProtection] = useState({
        whatsappFlowProtection: whatsappFlowProtection,
        templateProtection: templateProtection,
        campaignProtection: campaignProtection,
    })
    const kaiziqLeadsConnectionService = new KaiziqLeadsConnectionService();
    const whatsappPhoneNumberService = new WhatsappPhoneNumberService();
    const whatsappOAuthService = new WhatsappOAuthService();
    const errorHandler = new ErrorHandler();
    const navigate = useNavigate();
    const location = useLocation();
    const DOWNLOAD_FILE_PATH = '/system_token.pdf';
    const prefix = 'whatsapp-leads-settings';

    const isDeleteEnabled = confirmText === "Delete my whatsapp account";

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        setLoading(true);
        try {
            fetchNumberList();
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
    }

    const fetchNumberList = async (leadsId) => {
        setCursor('wait');
        try {
            let response = await kaiziqLeadsConnectionService.getLeadsAndPhoneNumberListByProjectId(currentProjectId);
            if(response.status < 400) {
                response = JSON.parse(response.body);
                let leadsList = response.leadsList;
                let phoneNumbers = response.phoneNumberList;
                setLeadsList(leadsList);
                if(leadsList.length > 0) {
                    setNumbersList(phoneNumbers);
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleChangePhoneNumber = (e) => {
        let id = e.target.value;
        if (!!id) {
            let findNumber = numbersList.find((item) => item.id === id);
            if (!!findNumber) {
                setPhoneNumber(findNumber);
            }
        }
    };

    const handleLeadsChange = (e) => {
        setCursor('wait');
        let id = e.target.value;
        if (!!id) {
            let leadsData = leadsList.find((item) => item.id === id);
            if (!!leadsData) {
                setCurrentLeads(leadsData);
                fetchNumberList(id);
            }
        }
        setCursor('auto');
    }

    const handleDeleteAccount = async () => {
        setCursor('wait');
        let bodyObj = {
            leadsId: currentLeadsId,
        };
        try {
            let response = await kaiziqLeadsConnectionService.deleteKaiziqLeadsConnection(bodyObj);
            if (response.status < 400) {
                response = JSON.parse(response.body);
                SnackbarManager.show(response.message, 'success');
                let leads = await kaiziqLeadsConnectionService.getLeadsConnectionListByProjectId(currentProjectId);
                if (leads.status < 400) {
                    let leadsList = JSON.parse(leads.body);
                    if (leadsList.length > 0) {
                        setCurrentLeads(leadsList[0]);
                    } else {
                        clearLeads();
                    }
                } else {
                    clearLeads();
                }
                navigate('/home/whatsapp')
            } else {
                SnackbarManager.show(response.message, 'error');
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleTokenUpdate = async (e) => {
        setCursor('wait');
        e.preventDefault();
        try {
            if (tokenValidate()) {
                let bodyObj = {
                    leadsId: currentLeadsId,
                    accessToken: Buffer.from(tokenValue).toString('base64'),
                }
                let response = await kaiziqLeadsConnectionService.updateKaiziqLeadsConnectionToken(bodyObj);
                if (response.status < 400) {
                    SnackbarManager.show(response.message, 'success');
                    navigate('/home/whatsapp');
                } else {
                    SnackbarManager.show(response.message, 'error');
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const toggleProtection = (target, newValue)=> {
        let protectionObj = {...advanceProtection};
        if(target === 'all') {
            protectionObj = {
                whatsappFlowProtection: newValue,
                templateProtection: newValue,
                campaignProtection: newValue,
            }
        } else {
            let value = advanceProtection[target] ?? false;
            protectionObj[target] = !value;
        }
        setAdvanceProtection(protectionObj);
        saveAdvanceProtection(protectionObj);
    }

    const saveAdvanceProtection = async (protectionObj) => {
        setCursor('wait');
        try {
            let bodyObj = {
                leadsId: currentLeadsId,
                advanceProtection: protectionObj,
            }
            let response = await kaiziqLeadsConnectionService.updateAdvanceProtection(bodyObj);
            if (response.status < 400) {
                response = JSON.parse(response.body);
                setCurrentLeads(response);
                SnackbarManager.show("Updated", 'success');
            } else {
                SnackbarManager.show(response.message, 'error');
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const tokenValidate = () => {
        const newErrors = {};
        if (!tokenValue.trim()) {
            newErrors.tokenValue = 'Access Token is required';
        } else if (tokenValue.length < 10) {
            newErrors.tokenValue = 'Access token is not valid';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        let key = e.target.name;
        let sanitizedValue = DOMPurify.sanitize(e.target.value);
        setFormData((prevData) => ({ ...prevData, [key]: sanitizedValue }));
    }

    const handleSubmit = async (e) => {
        setCursor('wait');
        e.preventDefault();
        try {
            if (validateData()) {
                let bodyObj = {
                    accessToken: Buffer.from(formData.accessToken).toString('base64'),
                    wabaId: Buffer.from(formData.wabaId).toString('base64'),
                    projectId: currentProjectId,
                    title: formData.title
                };
                let response = await kaiziqLeadsConnectionService.createKaiziqLeadsConnection(bodyObj);
                if (response.status == 200) {
                    setCurrentLeads(JSON.parse(response.body));
                    SnackbarManager.show(response.message, 'success');
                    navigate('/home');
                } else {
                    SnackbarManager.show(response.message, 'error');
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.show(err, 'error');
        }
        setCursor('auto');
    };

    const validateData = () => {
        const newErrors = {};
        if (!formData?.accessToken.trim()) {
            newErrors.accessToken = 'Access Token is required';
        } else if (formData?.accessToken.length < 10) {
            newErrors.accessToken = 'Access token is not valid';
        }
        if (!formData?.wabaId.trim()) {
            newErrors.wabaId = 'WABA ID is required';
        } else if (formData?.wabaId.length < 6) {
            newErrors.wabaId = 'WABA ID is not valid';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLogin = async () => {
        setLoading(true);
        try {
            let response = await whatsappOAuthService.oAuthLogin();
            if(response.status < 400) {
                let url = response.body;
                window.location.href = url;
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const handlePdfDownload = async (e) => {
        e.preventDefault();
        const link = document.createElement('a');
        link.href = DOWNLOAD_FILE_PATH;
        link.download = 'Steps to generate token.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const registerNumber = async (phoneNumberId)=> {
        setCursor('wait');
        try {
            let body = {
                leadsId : currentLeadsId,
                phoneNumberId: phoneNumberId
            }
            let response = await whatsappPhoneNumberService.registerPhoneNumber(body);
            if(response.status < 400) {
                SnackbarManager.show('Number is Registered', 'success');
                fetchNumberList(currentLeads?.id);
            }else {
                SnackbarManager.show(response?.message, 'error');
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const getPhoneNumberQuality = (quality) => {
        switch (quality) {
            case 'GREEN':
                return {
                    label: 'High',
                    icon: <CircleCheck size={15} />,
                    className: 'high',
                };
            case 'YELLOW':
                return {
                    label: 'Medium',
                    icon: <TriangleAlert size={15} />,
                    className: 'medium',
                };
            case 'RED':
                return {
                    label: 'Low',
                    icon: <CircleX size={15} />,
                    className: 'low',
                };
            default:
                return {
                    label: 'Pending',
                    icon: <Clock3 size={15} />,
                    className: 'pending',
                };
        }
    };

    function AdvancedProtectionToggle({ enabled = false, onChange, labelText, disabled = false,}) {

        const handleClick = () => {
            if (disabled) return;
            onChange?.();
        };

        return (
            <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label={labelText}
                disabled={disabled}
                onClick={handleClick}
                className={`advanced-toggle ${
                    enabled
                        ? "advanced-toggle--enabled"
                        : "advanced-toggle--disabled"
                }`}
            >
                <span className="advanced-toggle__text">
                    {enabled ? "ON" : "OFF"}
                </span>
                <span className="advanced-toggle__switch">
                    <span className="advanced-toggle__knob" />
                </span>
            </button>
        );
    }

    const whatsappProtected = advanceProtection?.whatsappFlowProtection || advanceProtection?.campaignProtection || advanceProtection?.templateProtection;

    return (
        <div className={`${prefix}-container`} style={{ cursor: cursor }}>
            <aside className="whatsapp-leads-settings-sidebar">
                <nav className="whatsapp-leads-settings-sidebar__nav">
                    <p className="whatsapp-leads-settings-sidebar__nav-label">WhatsApp</p>
                    {
                        currentUserProjectPermissions.allowEdit &&
                            <button
                                className={`whatsapp-leads-settings-sidebar__nav-item ${activeTab === 'connection' ? 'active' : ''}`}
                                onClick={() => setActiveTab('connection')}
                            >
                                <Cable size={17} />
                                <span>Add WhatsApp</span>
                            </button>
                    }
                    <button
                        className={`whatsapp-leads-settings-sidebar__nav-item ${activeTab === 'whatsapp' ? 'active' : ''}`}
                        onClick={() => setActiveTab('whatsapp')}
                    >
                        <SquareUserRound size={17} />
                        <span>Numbers</span>
                    </button>
                    <button
                        className={`whatsapp-leads-settings-sidebar__nav-item ${activeTab === 'advanceProtection' ? 'active' : ''}`}
                        onClick={() => setActiveTab('advanceProtection')}
                    >
                        <ShieldCheck size={17} />
                        <span>Advance Protection</span>
                    </button>
                    {
                        currentUserProjectPermissions.allowEdit &&
                            <button
                                className={`whatsapp-leads-settings-sidebar__nav-item ${activeTab === 'token' ? 'active' : ''}`}
                                onClick={() => setActiveTab('token')}
                                disabled={!currentUserProjectPermissions.allowEdit}>
                                <ArrowDownToLine size={17} />
                                <span>Update Token</span>
                            </button>
                    }

                    {
                        currentUserProjectPermissions.allowDelete &&
                            <>
                                <p className="whatsapp-leads-settings-sidebar__nav-label" style={{ marginTop: '1.5rem' }}>Danger Zone</p>

                                <button
                                    className={`whatsapp-leads-settings-sidebar__nav-item danger ${activeTab === 'delete' ? 'active-danger' : ''}`}
                                    onClick={() => setActiveTab('delete')}
                                    disabled={!currentUserProjectPermissions.allowDelete}>
                                    <Trash size={17} />
                                    <span>Delete Account</span>
                                </button>
                            </>
                    }
                </nav>
            </aside>

            <main className="whatsapp-leads-settings-main">
                {activeTab === 'connection' && (
                    <div className="whatsapp-leads-settings-section" style={{ cursor: loading ? 'wait' : 'auto' }}>
                        <div className="whatsapp-leads-settings-connect-card">
                            <div className="whatsapp-leads-settings-connect-card__icon-wrap">
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                                    alt="WhatsApp Logo"
                                    className="whatsapp-leads-settings-connect-card__logo"
                                />
                            </div>
                            <h2 className="whatsapp-leads-settings-connect-card__title">Connect WhatsApp</h2>
                            <p className="whatsapp-leads-settings-connect-card__desc">
                                Connect your WhatsApp account securely using Facebook OAuth to get started.
                            </p>
                            <button
                                className={`whatsapp-leads-settings-btn whatsapp-leads-settings-btn--primary whatsapp-leads-settings-btn--lg${loading ? ' disabled' : ''}`}
                                onClick={handleLogin}
                                disabled={loading}
                            >
                                {loading ? <RefreshCw size={16} className="spin" /> : <Cable size={16} />}
                                {loading ? 'Connecting…' : 'Connect WhatsApp'}
                            </button>
                            <p className="whatsapp-leads-settings-connect-card__footnote">
                                <ShieldCheck size={13} /> Your credentials are never stored.
                            </p>
                        </div>
                    </div>
                )}
                {activeTab === 'whatsapp' && (
                    <div className="whatsapp-leads-settings-section">
                        <div className="whatsapp-leads-settings-card">
                            <div className="whatsapp-leads-settings-card__header">
                                <SquareUserRound size={16} className="whatsapp-leads-settings-card__header-icon" />
                                <div>
                                    <h3 className="whatsapp-leads-settings-card__title">Available WhatsApp Numbers ({numbersList.length})</h3>
                                    <p className="whatsapp-leads-settings-card__desc">
                                        Select a WhatsApp number to use for sending and receiving messages. You can switch between numbers anytime.
                                    </p>
                                </div>
                                <Link target='_blank'
                                    to={'https://business.facebook.com/latest/whatsapp_manager/phone_numbers/?business_id=' + currentLeads?.businessId + '&asset_id=' + currentLeads?.wabaId }>
                                    <Settings size={16}/>
                                </Link>
                            </div>
                            <div className="whatsapp-leads-settings-numbers-list">
                                {numbersList.map((item) => {
                                const quality = getPhoneNumberQuality(item.quality_rating);
                                return(
                                    <label
                                        key={item.id}
                                        className={`whatsapp-leads-settings-number-item ${currentPhoneNumber?.id === item.id ? 'selected' : ''}`}
                                    >
                                        <div className="whatsapp-leads-settings-number-item__avatar">
                                            <SquareUserRound size={22} />
                                        </div>
                                        <div className="whatsapp-leads-settings-number-item__info">
                                            <div className='flex-row-center-0p5rem-gap'>
                                                <div className="whatsapp-leads-settings-number-item__name">{item.verified_name}</div>
                                                <div className="whatsapp-leads-settings-number-item__rating">
                                                    <div
                                                        className={`whatsapp-leads-settings-number-item__quality whatsapp-leads-settings-number-item__quality--${quality.className}`}>
                                                        {quality.icon}
                                                        <span>{quality.label}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="whatsapp-leads-settings-number-item__phone">Number · {item.display_phone_number}</div>
                                        </div>
                                        <div className="whatsapp-leads-settings-number-item__status">
                                            <Tooltip title="Connected" placement="top-start">
                                                <span className="whatsapp-leads-settings-badge whatsapp-leads-settings-badge--success">
                                                    <CheckCircle2 size={12} /> Connected
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Register Number" placement="top-start">
                                                <span
                                                    className="whatsapp-leads-settings-badge whatsapp-leads-settings-badge--warning"
                                                    onClick={() => {
                                                        setRegisterModal({
                                                            open: true,
                                                            item,
                                                        });
                                                    }}>
                                                    <AlertTriangle size={12} />
                                                    Register
                                                </span>
                                            </Tooltip>
                                        </div>
                                        <input
                                            type="checkbox"
                                            style={{opacity: '0', width: '0px'}}
                                            checked={currentPhoneNumber?.id === item.id}
                                            onChange={() => setPhoneNumber(item)}
                                            className="whatsapp-leads-settings-checkbox"
                                        />
                                    </label>
                                )})}
                            </div>
                        </div>
                        {registerModal.open && (
                            <div
                                className="register-number-modal__overlay"
                                onMouseDown={(e) => {
                                    if (e.target === e.currentTarget) {
                                        setRegisterModal({
                                            open: false,
                                            item: null,
                                        });
                                    }
                                }}>
                                <div
                                    className="register-number-modal"
                                    role="dialog"
                                    aria-modal="true"
                                    aria-labelledby="register-number-title">
                                    <div className="register-number-modal__header">
                                        <div className="register-number-modal__icon">
                                            <AlertTriangle size={22} />
                                        </div>
                                        <button
                                            type="button"
                                            className="register-number-modal__close"
                                            onClick={() =>
                                                setRegisterModal({
                                                    open: false,
                                                    item: null,
                                                })
                                            }
                                            aria-label="Close">
                                            <X size={18} />
                                        </button>
                                    </div>
                                    <div className="register-number-modal__content">
                                        <span className="register-number-modal__eyebrow">
                                            WHATSAPP PHONE NUMBER
                                        </span>
                                        <h3 id="register-number-title">
                                            Register this phone number
                                        </h3>
                                        <p className="register-number-modal__description">
                                            This WhatsApp phone number needs to be registered
                                            before you can use it to send messages and run
                                            WhatsApp campaigns.
                                        </p>
                                        <div className="register-number-modal__number-card">
                                            <div className="register-number-modal__number-icon">
                                                <MessageCircle size={18} />
                                            </div>
                                            <div className="register-number-modal__number-info">
                                                <span>Phone number</span>
                                                <strong>
                                                    {registerModal.item?.phone_number ||
                                                        registerModal.item?.display_phone_number ||
                                                        registerModal.item?.phoneNumber ||
                                                        "WhatsApp number"}
                                                </strong>
                                            </div>
                                        </div>
                                        <div className="register-number-modal__info">
                                            <div className="register-number-modal__info-icon">
                                                <Info size={16} />
                                            </div>
                                            <div>
                                                <strong>What happens next?</strong>
                                                <ul>
                                                    <li>
                                                        The number will be registered with
                                                        WhatsApp.
                                                    </li>
                                                    <li>
                                                        Once registration is complete, you can
                                                        use it for messaging.
                                                    </li>
                                                    <li>
                                                        Make sure this is the correct number
                                                        before continuing.
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="register-number-modal__footer">
                                        <button
                                            type="button"
                                            className="register-number-modal__cancel"
                                            onClick={() =>
                                                setRegisterModal({
                                                    open: false,
                                                    item: null,
                                                })
                                            }
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="register-number-modal__register"
                                            onClick={() => {
                                                registerNumber(registerModal.item.id);

                                                setRegisterModal({
                                                    open: false,
                                                    item: null,
                                                });
                                            }}>
                                            <CheckCircle2 size={16} />
                                            Register Number
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {activeTab === 'advanceProtection' && (
                    <div className="advanced-protection">
                        <div className="advanced-protection__card">
                            <div className="advanced-protection__header">
                                <div className="advanced-protection__brand">
                                    <div className="advanced-protection__shield">
                                        <ShieldCheck />
                                    </div>
                                    <div>
                                        <div className="advanced-protection__title-row">
                                            <h3>Advanced Protection</h3>

                                            {whatsappProtected && (
                                                <span className="advanced-protection__live">
                                                    <span />
                                                    Protection active
                                                </span>
                                            )}
                                        </div>
                                        <p>
                                            Automatically protect your WhatsApp campaigns from
                                            duplicate messages and unwanted communication.
                                        </p>
                                    </div>
                                </div>
                                <AdvancedProtectionToggle
                                    enabled={whatsappProtected}
                                    onChange={(e)=> toggleProtection('all', !whatsappProtected)}
                                    labelText='Advanced Protection'
                                    disabled={cursor === 'wait'}
                                />
                            </div>
                            <div className="advanced-protection__summary">
                                <div className="advanced-protection__summary-icon">
                                    <Info />
                                </div>
                                <div>
                                    <strong>
                                        {whatsappProtected
                                            ? "Your messages are protected automatically"
                                            : "Turn this on to protect your messaging"}
                                    </strong>
                                    <span>
                                        {whatsappProtected
                                            ? "No additional setup is required. The rules below will be applied automatically."
                                            : "Enable Advanced Protection to automatically apply messaging safety rules."}
                                    </span>
                                </div>
                            </div>
                            <div className="advanced-protection__body">
                                <div className="advanced-protection__section-heading">
                                    <div>
                                        <span className="advanced-protection__eyebrow">
                                            WHAT'S INCLUDED
                                        </span>

                                        <h4>Protection that works in the background</h4>
                                    </div>
                                </div>
                                <div className="advanced-protection__features">
                                    <div className="advanced-protection__feature">
                                        <div className="advanced-protection__feature-top">
                                            <div className="advanced-protection__feature-icon advanced-protection__feature-icon--blue">
                                                <Spotlight size={16} />
                                            </div>
                                            <AdvancedProtectionToggle
                                                enabled={advanceProtection?.campaignProtection ?? false}
                                                onChange={(e)=> toggleProtection('campaignProtection')}
                                                labelText='Campaign Protection'
                                                disabled={cursor === 'wait'}
                                            />
                                        </div>
                                        <h5>Campaign protection</h5>
                                        <p>
                                            A contact won't receive the same campaign template
                                            more than once from the same WhatsApp number within
                                            the same day.
                                        </p>
                                        <div className="advanced-protection__rule-preview">
                                            <div className="advanced-protection__rule-preview-row">
                                                <span>Same contact</span>
                                                <CheckCircleIcon />
                                            </div>

                                            <div className="advanced-protection__rule-preview-row">
                                                <span>Same number</span>
                                                <CheckCircleIcon />
                                            </div>

                                            <div className="advanced-protection__rule-preview-row">
                                                <span>Same day</span>
                                                <CheckCircleIcon />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="advanced-protection__feature">
                                        <div className="advanced-protection__feature-top">
                                            <div className="advanced-protection__feature-icon advanced-protection__feature-icon--orange">
                                                <BotMessageSquare size={16} />
                                            </div>
                                            <AdvancedProtectionToggle
                                                enabled={advanceProtection?.whatsappFlowProtection ?? false}
                                                onChange={(e)=> toggleProtection('whatsappFlowProtection')}
                                                labelText='Whatsapp Flow Protection'
                                                disabled={cursor === 'wait'}
                                            />
                                        </div>
                                        <h5>Whatsapp flow user opt-out</h5>
                                        <p>
                                            If a customer replies with an opt-out request, they'll
                                            automatically be unsubscribed from future messages.
                                        </p>
                                        <div className="advanced-protection__keywords">
                                            <span>STOP</span>
                                            <span>UNSUBSCRIBE</span>
                                            <span>NOT INTERESTED</span>
                                            <span>+ more</span>
                                        </div>
                                    </div>
                                    <div className="advanced-protection__feature">
                                        <div className="advanced-protection__feature-top">
                                            <div className="advanced-protection__feature-icon advanced-protection__feature-icon--green">
                                                <LayoutTemplate size={16} />
                                            </div>
                                            <AdvancedProtectionToggle
                                                enabled={advanceProtection?.templateProtection ?? false}
                                                onChange={(e)=> toggleProtection('templateProtection')}
                                                labelText='Template Protection'
                                                disabled={cursor === 'wait'}
                                            />
                                        </div>
                                        <h5>Built-in opt-out footer</h5>
                                        <p>
                                            Your templates include a clear way for
                                            customers to opt out of future messages.
                                        </p>
                                        <div className="advanced-protection__whatsapp-preview">
                                            <div className="advanced-protection__message">
                                                <div className="advanced-protection__message-text">
                                                    Your campaign message will appear here...
                                                </div>

                                                <div className="advanced-protection__message-footer">
                                                    Reply "STOP" to opt out
                                                </div>

                                                <div className="advanced-protection__message-time">
                                                    10:42 AM
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="advanced-protection__footer">
                                <div className="advanced-protection__footer-info">
                                    <Info />
                                    <span>
                                        Protection is applied automatically when this setting is
                                        enabled. You can manage individual template behavior from
                                        the template editor.
                                    </span>
                                </div>
                                {advanceProtection && (
                                    <div className="advanced-protection__enabled">
                                        <CheckCircleIcon />
                                        Advanced Protection is enabled
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {activeTab === 'token' && (
                    <div className="whatsapp-leads-settings-section">
                        <div className="whatsapp-leads-settings-card">
                            <div className="whatsapp-leads-settings-card__header">
                                <div style={{ flex: 1 }}>
                                    <h3 className="whatsapp-leads-settings-card__title">Update WhatsApp Token</h3>
                                    <p className="whatsapp-leads-settings-card__desc">
                                        Enter your new WhatsApp API token below. This token is required for sending campaigns and messages. <br/>
                                        <span style={{ color: '#d32f2f' }}>Never share your token with anyone.</span>
                                    </p>
                                </div>
                                <Tooltip title="Steps to generate token" placement="top-start">
                                    <button className="whatsapp-leads-settings-icon-btn" onClick={handlePdfDownload}>
                                        <ArrowDownToLine size={18} />
                                    </button>
                                </Tooltip>
                            </div>
                            <div className="whatsapp-leads-settings-form-group">
                                <Input
                                    label="Token"
                                    placeholder="Paste your new Token"
                                    type="text"
                                    icon={TokenIcon}
                                    required
                                    value={tokenValue}
                                    onChange={(e) => setTokenValue(e.target.value)}
                                    error={errors.tokenValue}
                                />
                                <button
                                    className={`whatsapp-leads-settings-btn whatsapp-leads-settings-btn--primary${cursor === 'wait' ? ' disabled' : ''}`}
                                    disabled={cursor === 'wait'}
                                    onClick={handleTokenUpdate}
                                >
                                    {cursor === 'wait' ? <RefreshCw size={15} className="spin" /> : null}
                                    Update Token
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {activeTab === 'delete' && (
                    <div className="whatsapp-leads-settings-section">
                        <div className="whatsapp-leads-settings-card whatsapp-leads-settings-card--danger">
                            <div className="whatsapp-leads-settings-card__header">
                                <Trash size={16} className="whatsapp-leads-settings-card__header-icon whatsapp-leads-settings-card__header-icon--danger" />
                                <div>
                                    <h3 className="whatsapp-leads-settings-card__title whatsapp-leads-settings-card__title--danger">
                                        Permanently Delete WhatsApp Account
                                    </h3>
                                    <p className="whatsapp-leads-settings-card__desc">
                                        This action cannot be undone. All your personal data, campaigns, and WhatsApp numbers will be permanently deleted from our system.
                                    </p>
                                </div>
                            </div>
                            <div className="whatsapp-leads-settings-alert whatsapp-leads-settings-alert--warning">
                                <AlertTriangle size={16} />
                                <span><b>Warning:</b> Deleting your WhatsApp account is irreversible. Please confirm your decision below.</span>
                            </div>
                            <div className="whatsapp-leads-settings-delete-form">
                                <label className="whatsapp-leads-settings-label">
                                    Type <strong>Delete my whatsapp account</strong> to confirm deletion:
                                </label>
                                <Input
                                    placeholder="Delete my whatsapp account"
                                    type="text"
                                    icon={TokenIcon}
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                />
                                <button
                                    className={`whatsapp-leads-settings-btn whatsapp-leads-settings-btn--danger whatsapp-leads-settings-btn--lg whatsapp-leads-settings-btn--full${(!isDeleteEnabled || cursor === 'wait') ? ' disabled' : ''}`}
                                    disabled={!isDeleteEnabled || cursor === 'wait'}
                                    onClick={handleDeleteAccount}
                                >
                                    <Trash size={16} />
                                    Delete My WhatsApp Account Permanently
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}